'use client';

/**
 * YouTube Video Modal
 *
 * Modal component for displaying YouTube videos with full tracking support.
 * Uses YouTube IFrame API for player control and analytics integration.
 */

import React, { Suspense, useEffect, useCallback, useRef, useMemo, useState } from 'react';
import { Modal } from '../Modal';
import { IconCta } from '../../../composables';
import { useYouTubeAPI, YT_PLAYER_STATES, getYouTubeVideoId } from '../../../hooks';
import { useVideoTracking } from '../../../hooks/useVideoTracking';
import { createVideoAnalyticsConfig } from '../../../utils/analytics/videoHelpers';
import type { MediaSource, AnalyticsProps } from '../../../types';

interface YouTubeModalProps {
  isOpen: boolean;
  src: string;
  onClose: () => void;
  mediaSource: MediaSource & { type: 'embed' };
  videoMetadata?: {
    title?: string;
    id?: string;
    duration?: number;
  };
  analyticsConfig?: AnalyticsProps;
}

export const YouTubeModal: React.FC<YouTubeModalProps> = ({
  isOpen,
  onClose,
  src,
  mediaSource,
  videoMetadata,
  analyticsConfig,
}) => {
  const isYouTubeAPIReady = useYouTubeAPI();
  const playerRef = useRef<YT.Player | null>(null);
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [youtubeContainer, setYoutubeContainer] = useState<HTMLDivElement | null>(null);

  // Generate tracking config internally
  const trackingConfig = useMemo(() => {
    const videoData = createVideoAnalyticsConfig(
      mediaSource,
      videoMetadata,
      analyticsConfig?.sectionHeader
    );

    return {
      ...videoData,
      moduleName: analyticsConfig?.moduleName || 'video-section',
    };
  }, [mediaSource, videoMetadata, analyticsConfig]);

  const { trackVideoStart, trackVideoProgress, trackVideoPause, trackVideoExit, markVideoResumed } =
    useVideoTracking(trackingConfig);

  /**
   * Initialize YouTube player with tracking
   */
  useEffect(() => {
    if (!isOpen || !isYouTubeAPIReady || !youtubeContainer) {
      return;
    }

    const videoId = getYouTubeVideoId(src);
    if (!videoId) return;

    // Destroy existing player if any
    if (playerRef.current) {
      playerRef.current.destroy();
      playerRef.current = null;
    }

    // Small delay to ensure DOM is fully ready
    const timeoutId = setTimeout(() => {
      if (!youtubeContainer) {
        return;
      }

      // Create YouTube player using the element reference
      // YouTube API will replace the div with its own iframe
      playerRef.current = new window.YT.Player(youtubeContainer, {
        videoId,
        playerVars: {
          autoplay: 1,
          playsinline: 1,
          enablejsapi: 1,
          rel: 0,
          modestbranding: 1,
        },
        events: {
          onReady: (event: YT.PlayerEvent) => {
            const duration = event.target.getDuration();
            trackVideoStart(duration);
          },
          onStateChange: (event: YT.OnStateChangeEvent) => {
            const player = event.target;
            const duration = player.getDuration();
            const currentTime = player.getCurrentTime();

            switch (event.data) {
              case YT_PLAYER_STATES.PLAYING:
                markVideoResumed();

                // Start progress tracking interval
                if (progressIntervalRef.current) {
                  clearInterval(progressIntervalRef.current);
                }

                progressIntervalRef.current = setInterval(() => {
                  const time = player.getCurrentTime();
                  trackVideoProgress(time, duration);
                }, 1000); // Check every second
                break;

              case YT_PLAYER_STATES.PAUSED:
                if (progressIntervalRef.current) {
                  clearInterval(progressIntervalRef.current);
                  progressIntervalRef.current = null;
                }
                trackVideoPause(currentTime, duration);
                break;

              case YT_PLAYER_STATES.ENDED:
                if (progressIntervalRef.current) {
                  clearInterval(progressIntervalRef.current);
                  progressIntervalRef.current = null;
                }
                // Track 100% completion
                trackVideoProgress(duration, duration);
                break;
            }
          },
        },
      });
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
      if (playerRef.current) {
        playerRef.current.destroy();
        playerRef.current = null;
      }
    };
  }, [
    isOpen,
    isYouTubeAPIReady,
    src,
    youtubeContainer,
    trackVideoStart,
    trackVideoProgress,
    trackVideoPause,
    markVideoResumed,
  ]);

  /**
   * Handle modal close with exit tracking
   */
  const handleClose = useCallback(
    (method: 'close_button' | 'escape' | 'backdrop' = 'close_button') => {
      trackVideoExit(method);

      // Clear progress interval
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }

      onClose();
    },
    [trackVideoExit, onClose]
  );

  const handleCloseButton = useCallback(() => {
    handleClose('close_button');
  }, [handleClose]);

  const handleBackdropClick = useCallback(() => {
    handleClose('backdrop');
  }, [handleClose]);

  // Handle ESC key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        handleClose('escape');
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleClose]);

  return (
    <Suspense fallback={null}>
      {isOpen && (
        <Modal isOpen={isOpen} onClose={handleBackdropClick} className="modal-player__modal">
          <IconCta
            icon="close"
            onClick={handleCloseButton}
            ariaLabel="Close video"
            className="modal-player__close-button"
            size="large"
          />

          <div className="modal-player__video-content">
            <div ref={setYoutubeContainer} className="modal-player__iframe" />
          </div>
        </Modal>
      )}
    </Suspense>
  );
};
